import { Property } from "@/features/survey/types/survey.types";
import { MAP_STYLES } from "@/shared/utils/constants";
import { getIconData } from "@/shared/utils/map";
import mapboxgl from "mapbox-gl";
import { useEffect, useRef } from "react";

// Configure Mapbox access token
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const FALLBACK_MAPBOX_TOKEN =
  "pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw";

// Set the access token
if (MAPBOX_TOKEN && MAPBOX_TOKEN.startsWith("pk.")) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
} else {
  mapboxgl.accessToken = FALLBACK_MAPBOX_TOKEN;
}

interface PropertyMapProps {
  property: Property;
}

const PropertyMap = ({ property }: PropertyMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  const lat =
    typeof property.latitude === "string"
      ? parseFloat(property.latitude)
      : property.latitude;
  const lng =
    typeof property.longitude === "string"
      ? parseFloat(property.longitude)
      : property.longitude;

  // Always use emeraldEsriHybrid style
  const mapStyle = (() => {
    const emeraldEsriHybridStyle = MAP_STYLES.find(
      (style) => style.id === "emeraldEsriHybrid",
    );
    return (
      emeraldEsriHybridStyle?.url ||
      "mapbox://styles/mapbox/satellite-streets-v12"
    );
  })();

  useEffect(() => {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;
    if (map.current) return; // Initialize map only once
    if (!mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [lng, lat],
      zoom: 12,
    });

    // Add error handling for style loading issues (including sprite 404 errors)
    map.current.on("error", (e) => {
      console.warn("Map style error detected:", e.error);

      // Check for sprite loading errors or other style-related issues
      if (
        map.current &&
        (e.error.message.includes("sprite") ||
          e.error.message.includes("404") ||
          e.error.message.includes("Source layer") ||
          e.error.message.includes("does not exist"))
      ) {
        console.warn("Falling back to satellite view due to style error");
        // Fallback to a reliable Mapbox style
        map.current.setStyle("mapbox://styles/mapbox/satellite-streets-v12");
      }
    });

    // Get the icon color for the property
    const iconColor = getIconData(property).iconColor;

    // Create a custom circular marker element
    const markerElement = document.createElement("div");
    markerElement.className = "custom-marker";
    markerElement.style.cssText = `
      width: 25px;
      height: 25px;
      background-color: ${iconColor};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      cursor: pointer;
    `;

    // Add the custom marker to the map
    if (lng == null || lat == null || isNaN(lng) || isNaN(lat)) return;
    new mapboxgl.Marker({ element: markerElement })
      .setLngLat([lng, lat])
      .addTo(map.current);

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [lat, lng, mapStyle, property]);

  if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
    return (
      <div
        className={`h-[430px] flex items-center justify-center rounded-lg p-4 lg:p-10`}
      >
        <p className="text-gray-500">Map not available</p>
      </div>
    );
  }

  return (
    <div className={`w-full h-[430px]`}>
      <div ref={mapContainer} className="w-full h-full lg:rounded-lg" />
    </div>
  );
};

export default PropertyMap;
